import type { Dict } from '../index.js';

/** Arabic: common. Keys are the exact English text. */
export const common: Dict = {
  // --- General ---
  'Loading…': 'جارٍ التحميل…',
  'Starting…': 'جارٍ التشغيل…',
  Save: 'حفظ',
  Cancel: 'إلغاء',
  Confirm: 'تأكيد',
  Close: 'إغلاق',
  Export: 'تصدير',
  Reason: 'السبب',
  'Recorded in the audit log.': 'يُسجَّل في سجل التدقيق.',
  '{from}–{to} of {total}': '{from}–{to} من {total}',
  'Previous page': 'الصفحة السابقة',
  'Next page': 'الصفحة التالية',
  '{n} people': '{n} أشخاص',
  system: 'النظام',
  Pending: 'قيد الانتظار',
  Approved: 'موافَق عليها',
  'Excel (.xlsx)': 'Excel (.xlsx)',
  CSV: 'CSV',
  PDF: 'PDF',

  // --- Roles ---
  Administrator: 'مسؤول النظام',
  Manager: 'المدير',
  Employee: 'موظف',

  // --- Navigation ---
  Dashboard: 'لوحة المعلومات',
  Employees: 'الموظفون',
  Departments: 'الأقسام',
  Attendance: 'الحضور',
  Leave: 'الإجازات',
  'Vacation Balances': 'أرصدة الإجازات',
  Productivity: 'الإنتاجية',
  Reports: 'التقارير',
  Payroll: 'الرواتب',
  Schedules: 'جداول العمل',
  Holidays: 'العطلات',
  Settings: 'الإعدادات',
  'Audit Log': 'سجل التدقيق',
  'My Team': 'فريقي',
  'Team Attendance': 'حضور الفريق',
  'Leave Approvals': 'الموافقة على الإجازات',
  'Team Balances': 'أرصدة الفريق',
  'Team Productivity': 'إنتاجية الفريق',
  'Team Reports': 'تقارير الفريق',
  'My Attendance': 'حضوري',
  'My Leave': 'إجازاتي',
  'My Balance': 'رصيدي',
  'My Productivity': 'إنتاجيتي',
  'My Profile': 'ملفي الشخصي',
  Me: 'أنا',

  // --- Layout / header ---
  'Employee time': 'توقيت الموظف',
  "Show each record in that employee's own time zone": 'عرض كل سجل حسب المنطقة الزمنية للموظف نفسه',
  'Company time': 'توقيت الشركة',
  'Show everything in company time ({zone})': 'عرض كل شيء بتوقيت الشركة ({zone})',
  'My time': 'توقيتي',
  'Show everything in your time zone ({zone})': 'عرض كل شيء حسب منطقتك الزمنية ({zone})',
  'Time display': 'عرض الوقت',
  'Time display mode': 'طريقة عرض الوقت',
  'Notifications, {n} unread': 'الإشعارات، {n} غير مقروءة',
  Notifications: 'الإشعارات',
  'Mark all read': 'تحديد الكل كمقروء',
  "You're all caught up.": 'لا يوجد شيء جديد.',
  'Sign out': 'تسجيل الخروج',
  'Close menu': 'إغلاق القائمة',
  'Open menu': 'فتح القائمة',
  'My profile': 'ملفي الشخصي',

  // --- Sign in / passwords ---
  'Company logo': 'شعار الشركة',
  'Sign in to Workforce': 'تسجيل الدخول إلى Workforce',
  'Leave, attendance, payroll and time tracking': 'الإجازات والحضور والرواتب وتتبع الوقت',
  'E-mail': 'البريد الإلكتروني',
  Password: 'كلمة المرور',
  'Sign in': 'تسجيل الدخول',
  'Forgot your password? Ask your administrator to reset it.': 'نسيت كلمة المرور؟ اطلب من مسؤول النظام إعادة تعيينها.',
  'Choose a new password': 'اختر كلمة مرور جديدة',
  '{email} — your password was set by an administrator. Please choose your own before continuing.':
    '{email} — عيّن مسؤول النظام كلمة المرور الخاصة بك. يُرجى اختيار كلمة مرور خاصة بك قبل المتابعة.',
  'Password changed. Other devices have been signed out.': 'تم تغيير كلمة المرور. وتم تسجيل الخروج من الأجهزة الأخرى.',
  'Current password': 'كلمة المرور الحالية',
  'New password': 'كلمة المرور الجديدة',
  'At least 10 characters, using three of: lower-case, upper-case, digits, symbols.':
    '10 أحرف على الأقل، وتتضمن ثلاثة مما يلي: أحرف صغيرة، أحرف كبيرة، أرقام، رموز.',
  'Confirm new password': 'تأكيد كلمة المرور الجديدة',
  'Passwords do not match': 'كلمتا المرور غير متطابقتين',
  'Change password': 'تغيير كلمة المرور',

  // --- Push notifications ---
  'On iPhone: open this site in Safari, tap Share → {action}, open the app from the home screen, then come back here (needs iOS 16.4 or later).':
    'على iPhone: افتح هذا الموقع في Safari، واضغط مشاركة ← {action}، ثم افتح التطبيق من الشاشة الرئيسية وعُد إلى هنا (يتطلب iOS 16.4 أو أحدث).',
  'Notifications on this device': 'الإشعارات على هذا الجهاز',
  'Get leave approvals, missing clock-out reminders and reports as phone or computer notifications, even when the app is closed. Only a title and a short line are sent.':
    'استلم الموافقات على الإجازات وتذكيرات تسجيل الانصراف الناقص والتقارير كإشعارات على الهاتف أو الكمبيوتر، حتى عندما يكون التطبيق مغلقًا. يُرسَل عنوان وسطر قصير فقط.',
  'Add to Home Screen': 'إضافة إلى الشاشة الرئيسية',
  'This browser does not support notifications.': 'هذا المتصفح لا يدعم الإشعارات.',
  'Notifications are blocked for this site. Allow them in the browser or phone settings, then reload this page.':
    'الإشعارات محظورة لهذا الموقع. اسمح بها من إعدادات المتصفح أو الهاتف، ثم أعد تحميل هذه الصفحة.',
  'On for this device': 'مفعّلة على هذا الجهاز',
  'Send a test': 'إرسال تجربة',
  'Turn off': 'إيقاف',
  'Sent — it should arrive within a few seconds.': 'تم الإرسال — يُفترض أن يصل خلال ثوانٍ.',
  'Turn on notifications': 'تفعيل الإشعارات',

  // --- Coming soon / not found ---
  'Built in Phase {phase}': 'يُبنى في المرحلة {phase}',
  'This section is part of a later phase.': 'هذا القسم جزء من مرحلة لاحقة.',
  'Leave types, policies, balances, apply-with-preview and the approval workflow.':
    'أنواع الإجازات والسياسات والأرصدة وتقديم الطلب مع المعاينة ومسار الموافقة.',
  'Clock in/out, fingerprint-device import, daily attendance with late/early/missing detection.':
    'تسجيل الحضور والانصراف، والاستيراد من جهاز البصمة، والحضور اليومي مع رصد التأخير والانصراف المبكر والتسجيل الناقص.',
  'Configurable reporting periods (default 25th → 24th) and month-end summaries.':
    'فترات قابلة للتخصيص (افتراضيًا من يوم 25 إلى يوم 24) وملخصات نهاية الشهر.',
  'Productivity dashboards fed by the Windows desktop agent.': 'لوحات الإنتاجية المغذّاة من برنامج سطح المكتب على Windows.',
  'Page not found': 'الصفحة غير موجودة',
  "The page you're looking for doesn't exist, or you don't have access to it.": 'الصفحة التي تبحث عنها غير موجودة، أو ليست لديك صلاحية الوصول إليها.',

  // --- Dashboard (management) ---
  'Hello, {name}': 'مرحبًا، {name}',
  'Your team at a glance': 'نظرة سريعة على فريقك',
  '{company} at a glance': 'نظرة سريعة على {company}',
  'Team members': 'أعضاء الفريق',
  'Total employees': 'إجمالي الموظفين',
  'Currently working': 'يعملون الآن',
  '{n} arrived late': '{n} وصلوا متأخرين',
  'On leave today': 'في إجازة اليوم',
  'Absent / not in yet': 'غائب / لم يحضر بعد',
  'Past their start time, no punch, not on leave': 'تجاوزوا موعد البدء، بلا تسجيل، وليسوا في إجازة',
  'Pending leave requests': 'طلبات الإجازة المعلّقة',
  'Review →': 'مراجعة ←',
  'All caught up': 'لا شيء معلّق',
  'Employees by country': 'الموظفون حسب الدولة',
  'No employees yet.': 'لا يوجد موظفون بعد.',
  'Employees by department': 'الموظفون حسب القسم',
  'Recent hires': 'المعيّنون حديثًا',
  'No employees yet': 'لا يوجد موظفون بعد',
  Alerts: 'التنبيهات',
  '{n} employees': '{n} موظفين',
  '{n} employee': 'موظف واحد',
  'without a login account.': 'بدون حساب دخول.',
  '{n} leave requests': '{n} طلبات إجازة',
  '{n} leave request': 'طلب إجازة واحد',
  'waiting for approval.': 'بانتظار الموافقة.',
  '{n} on probation.': '{n} تحت الاختبار.',
  'has a missing clock-in/out on {date}.': 'لديه تسجيل حضور/انصراف ناقص في {date}.',
  'Late today: {names}': 'المتأخرون اليوم: {names}',
  "Who's off this week": 'الغائبون هذا الأسبوع',
  'Calendar →': 'التقويم ←',
  'Nobody is on approved leave in the next 7 days.': 'لا أحد في إجازة موافَق عليها خلال الأيام السبعة القادمة.',
  'Below the {pct}% target on {date}:': 'دون الهدف {pct}% في {date}:',
  '{time} of apps and websites not classified in the last 10 days —': '{time} من التطبيقات والمواقع غير مصنَّفة خلال آخر 10 أيام —',
  'classify them': 'صنّفها',
  'Productivity — {date}': 'الإنتاجية — {date}',
  'Daily report': 'التقرير اليومي',
  'No computer activity recorded in the last 10 days.': 'لم يُسجَّل أي نشاط على الكمبيوتر خلال آخر 10 أيام.',
  'Team score': 'نتيجة الفريق',
  Productive: 'منتج',
  Unproductive: 'غير منتج',
  Idle: 'خامل',
  '{n} person tracked.': 'شخص واحد مسجَّل.',
  '{n} people tracked.': '{n} أشخاص مسجَّلون.',
  'Below the {pct}% target: {names}.': 'دون الهدف {pct}%: {names}.',
  'Everyone at or above the {pct}% target.': 'الجميع عند الهدف {pct}% أو أعلى.',
  'Current reporting period': 'الفترة الحالية',
  'Report →': 'التقرير ←',
  '{n} day left': 'بقي يوم واحد',
  '{n} days left': 'المتبقي: {n} يوم',
  'Closed by {name} — figures are frozen.': 'أقفلها {name} — الأرقام مجمَّدة.',
  'Ended — review missing punches and pending leave, then close it.': 'انتهت — راجع التسجيلات الناقصة والإجازات المعلّقة، ثم أقفلها.',
  'Review and close →': 'المراجعة والإقفال ←',

  // --- Dashboard (employee) ---
  'Vacation balance': 'رصيد الإجازات',
  '{n} days': '{n} يوم',
  'of {n} this leave year': 'من أصل {n} لهذه السنة',
  'Pending requests': 'الطلبات المعلّقة',
  'Days taken': 'الأيام المستخدمة',
  'My work schedule': 'جدول عملي',
  '{time} per week · times in {tz}': '{time} أسبوعيًا · الأوقات حسب {tz}',
  'Upcoming change from {date}: {schedule} ({tz})': 'تغيير قادم اعتبارًا من {date}: {schedule} ({tz})',
  'No schedule assigned yet.': 'لم يُحدَّد جدول عمل بعد.',
  'My details': 'بياناتي',
  'Employee ID': 'الرقم الوظيفي',
  Department: 'القسم',
  Location: 'الموقع',
  'View full profile →': 'عرض الملف الكامل ←',
  'My leave': 'إجازاتي',
  'Request leave →': 'طلب إجازة ←',
  'No upcoming leave.': 'لا توجد إجازات قادمة.',

  // --- Audit log action names (lib/format.ts describeAction) ---
  'Signed in': 'تسجيل الدخول',
  'Signed out': 'تسجيل الخروج',
  'Failed sign-in': 'محاولة دخول فاشلة',
  'Changed password': 'تغيير كلمة المرور',
  'Login account created': 'إنشاء حساب دخول',
  'Login account changed': 'تعديل حساب دخول',
  'Password reset by admin': 'إعادة تعيين كلمة المرور من المسؤول',
  'Employee created': 'إضافة موظف',
  'Employee updated': 'تعديل بيانات موظف',
  'Employee deactivated': 'إيقاف موظف',
  'Employee reactivated': 'إعادة تفعيل موظف',
  'Employee deleted': 'حذف موظف',
  'Work terms changed': 'تعديل شروط العمل',
  'Work terms changed (retroactive)': 'تعديل شروط العمل (بأثر رجعي)',
  'Scheduled work-terms change removed': 'إلغاء تعديل مجدول لشروط العمل',
  'Employee settings changed': 'تعديل إعدادات موظف',
  'Company setting changed': 'تعديل إعداد الشركة',
  'Holidays synced from official source': 'مزامنة العطلات من المصدر الرسمي',
  'Attendance punch added by admin': 'إضافة تسجيل حضور من المسؤول',
  'Attendance punch voided': 'إلغاء تسجيل حضور',
  'Attendance imported from machine': 'استيراد الحضور من جهاز البصمة',
  'Attendance device added': 'إضافة جهاز حضور',
  'Attendance device changed': 'تعديل جهاز حضور',
  'Attendance device key replaced': 'استبدال مفتاح جهاز الحضور',
  'Salary set / changed': 'تحديد / تعديل الراتب',
  'Upcoming salary change removed': 'إلغاء تعديل قادم للراتب',
  'Payroll exchange rate set': 'تحديد سعر الصرف للرواتب',
  'Payroll adjustment added': 'إضافة تعديل على الرواتب',
  'Payroll adjustment removed': 'حذف تعديل على الرواتب',
  'Payroll finalised': 'اعتماد نهائي للرواتب',
  'Payroll reopened': 'إعادة فتح الرواتب',
  'Monitoring notice accepted': 'قبول إشعار المراقبة',
  'Computer enrolment code created': 'إنشاء رمز ربط كمبيوتر',
  'Computer connected': 'ربط كمبيوتر',
  'Computer disconnected': 'فصل كمبيوتر',
  'App/website classified': 'تصنيف تطبيق/موقع',
  'App/website classification changed': 'تعديل تصنيف تطبيق/موقع',
  'App/website classification removed': 'حذف تصنيف تطبيق/موقع',
  'Starter app list loaded': 'تحميل قائمة التطبيقات الأولية',
  'Old activity records deleted (retention)': 'حذف سجلات النشاط القديمة (مدة الاحتفاظ)',
  'Screenshot viewed': 'تم عرض لقطة الشاشة',
  'Screenshot deleted': 'تم حذف لقطة الشاشة',
  'Reporting period closed': 'الفترة مُقفلة',
  'Reporting period reopened': 'إعادة فتح الفترة',
  'Leave requested': 'تقديم طلب إجازة',
  'Leave approved': 'الموافقة على إجازة',
  'Leave recorded (no approval needed)': 'تسجيل إجازة (دون الحاجة لموافقة)',
  'Leave rejected': 'رفض إجازة',
  'Leave cancelled': 'إلغاء إجازة',
  'Leave balance adjusted': 'تعديل رصيد الإجازات',
  'Leave allowance changed': 'تعديل استحقاق الإجازات',
  'Leave type created': 'إنشاء نوع إجازة',
  'Leave type changed': 'تعديل نوع إجازة',
  'Leave type deleted': 'حذف نوع إجازة',
  'Leave policy created': 'إنشاء سياسة إجازات',
  'Leave policy changed': 'تعديل سياسة إجازات',
  'Leave policy deleted': 'حذف سياسة إجازات',
};
